import '../../i18n';
import {useTranslation} from 'react-i18next';
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { gql, useMutation } from '@apollo/client';
import styled from 'styled-components';
import { FiMoreHorizontal, FiSearch, FiX } from 'react-icons/fi';
import { getSearchUrl, getStoredSearchEngine } from '../../constants/searchEngines';

import { ENTITY_ICONS, ENTITY_LABELS, isMentionEntity } from '../Messages/EntityBadges';
import { ENTITY_COLORS, PALE_WHITE_1 } from '../../appearance/Colors';
import { standardMenuSurface, standardMenuItem } from '../../appearance/menuStyles';

const REJECT_MENTION = gql`
    mutation RejectMessageMention($messageId: ID!, $type: MessageEntityTypeEnum!, $name: String!) {
        rejectMessageMention(messageId: $messageId, type: $type, name: $name) {
            id
            entities {
                type name confidence artifactId
                enrichment { image width height aspect_ratio description year url source }
            }
        }
    }
`;

const EntityMentionsSection = ({ entities, messageId, canRemove = false, onSearch, inModal = false }) => {
    const {t: lnT} = useTranslation();

    const [rejectMention, { loading }] = useMutation(REJECT_MENTION);
    const [removed, setRemoved] = useState(new Set());
    const [failed, setFailed] = useState(false);
    const [activeMenu, setActiveMenu] = useState(null);
    const menuRef = useRef(null);
    const triggerRef = useRef(null);
    useEffect(() => {
        if (!activeMenu) return undefined;
        const dismissOutside = event => {
            if (!menuRef.current?.contains(event.target) && !triggerRef.current?.contains(event.target)) {
                setActiveMenu(null);
            }
        };
        const dismissOnEscape = event => {
            if (event.key === 'Escape') {
                event.stopPropagation();
                setActiveMenu(null);
                triggerRef.current?.focus();
            }
        };
        const dismissOnScroll = event => {
            if (!menuRef.current?.contains(event.target)) setActiveMenu(null);
        };
        const dismissOnResize = () => setActiveMenu(null);
        document.addEventListener('mousedown', dismissOutside);
        document.addEventListener('touchstart', dismissOutside);
        document.addEventListener('keydown', dismissOnEscape, true);
        document.addEventListener('scroll', dismissOnScroll, true);
        window.addEventListener('resize', dismissOnResize);
        return () => {
            document.removeEventListener('mousedown', dismissOutside);
            document.removeEventListener('touchstart', dismissOutside);
            document.removeEventListener('keydown', dismissOnEscape, true);
            document.removeEventListener('scroll', dismissOnScroll, true);
            window.removeEventListener('resize', dismissOnResize);
        };
    }, [activeMenu]);
    useEffect(() => { setActiveMenu(null); }, [messageId]);
    const openMenu = (entity, trigger) => {
        const key = mentionKey(entity);
        if (activeMenu?.key === key) return;
        triggerRef.current = trigger;
        const rect = trigger.getBoundingClientRect();
        const menuHeight = canRemove && messageId ? 80 : 42;
        setActiveMenu({ key, entity, position: {
            right: Math.max(8, Math.min(window.innerWidth - rect.right, window.innerWidth - 208)),
            ...(rect.top >= menuHeight + 8
                ? { bottom: window.innerHeight - rect.top + 8 }
                : { top: rect.bottom + 8 })
        } });
    };
    const mentionKey = entity => `${messageId}:${entity.type}:${entity.name}`;
    const mentions = Array.isArray(entities)
        ? entities.filter(entity => isMentionEntity(entity) && !removed.has(mentionKey(entity))) : [];
    const removeMention = async entity => {
        if (loading) return;
        setFailed(false);
        try {
            await rejectMention({ variables: { messageId, type: entity.type, name: entity.name } });
            setRemoved(previous => new Set([...previous, mentionKey(entity)]));
        } catch (_) {
            setFailed(true);
        }
    };

    const searchMention = entity => {
        if (onSearch) {
            onSearch(entity);
        } else {
            window.open(getSearchUrl(entity.name, getStoredSearchEngine()), '_blank', 'noopener,noreferrer');
        }
    };

    if (mentions.length === 0) {
        return null;
    }

    return (
        <Section $inModal={inModal}>
            <SectionHeader>
                <SectionTitle>{lnT('messages:mentions')}</SectionTitle>
                <SectionCount>{mentions.length}</SectionCount>
            </SectionHeader>
            {failed ? <p role="alert">{lnT('messages:removeMentionFailed')}</p> : null}
            <MentionList>
                {mentions.map((entity, index) => {
                    const type = entity.type || lnT('messages:other');
                    const colors = ENTITY_COLORS[type] || ENTITY_COLORS.other;
                    const label = ENTITY_LABELS[type] || lnT('common:other');
                    const EntityIcon = ENTITY_ICONS[type] || ENTITY_ICONS.other;

                    const enrichment = entity.enrichment;
                    const hasLink = Boolean(enrichment?.url);
                    const handleClick = () => {
                        if (hasLink) {
                            window.open(enrichment.url, '_blank', 'noopener,noreferrer');
                        } else {
                            searchMention(entity);
                        }
                    };
                    return (
                        <MentionItem key={`${type}-${entity.name}-${index}`}>
                            <MentionRow
                                type="button"
                                onClick={handleClick}
                                title={hasLink ? lnT('messages:openOn', { value1: String(entity.name), value2: String(enrichment?.source === 'omdb' ? 'IMDb' : enrichment?.source || 'external site') }) : lnT('messages:searchFor', { value1: String(entity.name) })}
                            >
                                {enrichment?.image ? (
                                    <MentionPoster
                                        src={enrichment.image}
                                        alt={entity.name}
                                        width={36}
                                        height={enrichment.aspect_ratio ? Math.round(36 * enrichment.aspect_ratio) : 52}
                                    />
                                ) : (
                                    <MentionIcon $colors={colors} aria-hidden="true">
                                        <EntityIcon size={15} />
                                    </MentionIcon>
                                )}
                                <MentionContent>
                                    <MentionName>
                                        {entity.name}
                                        {enrichment?.year ? <MentionYear> · {enrichment.year}</MentionYear> : null}
                                    </MentionName>
                                    <MentionType $colors={colors}>{label}</MentionType>
                                    {enrichment?.description ? (
                                        <MentionDescription>{enrichment.description}</MentionDescription>
                                    ) : null}
                                </MentionContent>
                            </MentionRow>
                            <MentionMenuButton
                                type="button"
                                $open={activeMenu?.key === mentionKey(entity)}
                                title={lnT('messages:moreMessageActions')}
                                aria-label={lnT('messages:moreMessageActions')}
                                aria-haspopup="menu"
                                aria-expanded={activeMenu?.key === mentionKey(entity)}
                                onClick={event => {
                                    event.stopPropagation();
                                    openMenu(entity, event.currentTarget);
                                }}
                            >
                                <FiMoreHorizontal size={16} />
                            </MentionMenuButton>
                        </MentionItem>
                    );
                })}
            </MentionList>
            {activeMenu ? createPortal(
                <MentionMenu
                    ref={menuRef}
                    role="menu"
                    style={activeMenu.position}
                    onMouseDown={event => event.stopPropagation()}
                    onClick={event => event.stopPropagation()}
                >
                    <MentionMenuItem type="button" role="menuitem" onClick={() => {
                        searchMention(activeMenu.entity);
                        setActiveMenu(null);
                    }}>
                        <FiSearch size={14} />{lnT('common:search')}
                    </MentionMenuItem>
                    {canRemove && messageId ? (
                        <MentionMenuItem
                            type="button"
                            role="menuitem"
                            $destructive
                            disabled={loading}
                            aria-disabled={loading}
                            title={lnT('messages:removeIncorrectMention', { name: activeMenu.entity.name })}
                            aria-label={lnT('messages:removeIncorrectMention', { name: activeMenu.entity.name })}
                            onClick={() => {
                                void removeMention(activeMenu.entity);
                                setActiveMenu(null);
                            }}
                        >
                            <FiX size={14} />{lnT('common:remove')}
                        </MentionMenuItem>
                    ) : null}
                </MentionMenu>, document.body
            ) : null}
        </Section>
    );
};

const Section = styled.section`
    margin: ${({ $inModal }) => ($inModal ? '0' : '20px 0 8px')};
    padding-top: ${({ $inModal }) => ($inModal ? '0' : '16px')};
    border-top: ${({ $inModal }) => ($inModal ? 'none' : '1px solid rgba(255, 255, 255, 0.1)')};
`;

const SectionHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 7px;
    margin-bottom: 9px;
`;

const SectionTitle = styled.h3`
    margin: 0;
    color: rgba(255, 255, 255, 0.58);
    font-size: 11px;
    font-weight: 700;
    line-height: 1.2;
    letter-spacing: 0.06em;
    text-transform: uppercase;
`;

const SectionCount = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.07);
    color: rgba(255, 255, 255, 0.56);
    font-size: 9px;
    font-weight: 700;
`;

const MentionList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 7px;
`;

const MentionItem = styled.div`
    position: relative;
    display: flex;
    align-items: center;
`;

const MentionMenuButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    right: 8px;
    width: 28px;
    height: 28px;
    opacity: ${({ $open }) => ($open ? 1 : 0)};
    transition: opacity 0.15s ease;
    ${MentionItem}:hover &, ${MentionItem}:focus-within & { opacity: 1; }
    @media (hover: none) { opacity: 1; }
    border: 0;
    border-radius: 8px;
    background: transparent;
    color: rgba(255, 255, 255, 0.55);
    cursor: pointer;
    &:hover { background: rgba(255, 255, 255, 0.08); color: white; }
    &:focus-visible { outline: 2px solid white; }
    &:disabled { opacity: 0.4; cursor: wait; }
`;

const MentionMenu = styled.div`
    ${standardMenuSurface}
    position: fixed;
    z-index: 10000;
    width: 200px;
    max-width: calc(100vw - 16px);
    font-size: 12px;
    line-height: 1.4;
`;

const MentionMenuItem = styled.button`
    ${standardMenuItem}
    color: ${({ $destructive }) => ($destructive ? '#ff7a7a' : 'rgba(255, 255, 255, 0.92)')};
    svg { flex: 0 0 auto; }
`;

const MentionRow = styled.button`
    width: 100%;
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 9px 44px 9px 10px;
    border: 1px solid ${PALE_WHITE_1};
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.03);
    color: rgba(255, 255, 255, 0.9);
    font: inherit;
    text-align: left;
    cursor: pointer;
    transition: background 0.15s ease, border-color 0.15s ease;

    &:hover {
        background: rgba(255, 255, 255, 0.065);
        border-color: rgba(255, 255, 255, 0.14);
    }

    &:focus-visible {
        outline: 2px solid rgba(255, 255, 255, 0.72);
        outline-offset: 2px;
    }
`;

const MentionIcon = styled.span`
    width: 32px;
    height: 32px;
    flex: 0 0 32px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    background: ${({ $colors }) => $colors.bg};
    border: 1px solid ${({ $colors }) => $colors.border};
    color: ${({ $colors }) => $colors.text};
`;

const MentionContent = styled.span`
    min-width: 0;
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

const MentionName = styled.span`
    overflow: hidden;
    color: rgba(255, 255, 255, 0.92);
    font-size: 12px;
    font-weight: 600;
    line-height: 1.35;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const MentionType = styled.span`
    color: ${({ $colors }) => $colors.text};
    font-size: 10px;
    font-weight: 500;
    line-height: 1.2;
`;

const MentionPoster = styled.img`
    flex: 0 0 36px;
    width: 36px;
    border-radius: 5px;
    object-fit: cover;
    background: rgba(255, 255, 255, 0.06);
    align-self: flex-start;
`;

const MentionYear = styled.span`
    font-weight: 400;
    color: rgba(255, 255, 255, 0.45);
`;

const MentionDescription = styled.span`
    font-size: 10px;
    color: rgba(255, 255, 255, 0.45);
    line-height: 1.45;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    margin-top: 2px;
`;

export default EntityMentionsSection;
